import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { apolloClient } from "@/graphql/client";
import { CreateStudioNotebookDocument } from "@/graphql/generated/operations";
import { nowIsoMinute } from "@/lib/date-instant";
import { GROW_NOTE_MAX_ITEMS, growNoteMessage, type GrowNoteCount } from "@/lib/grow-note";
import { startGrowNoteRun } from "@/lib/grow-note-run";
import {
  composeGrowNotebook,
  selectImproveExamples,
  splitImproveAnswer,
  type GrowNotebookHeadings,
} from "@/lib/grow-studio-notebook";
import { useAgentStore, type AgentChip, type AgentMessage, type GrowSaveTarget } from "@/stores/agent-store";

export type SaveToStudioChip = Extract<AgentChip, { kind: "save-to-studio" }>;

/** What a save the person typed asked for: the examples by index, ascending, and their wishes (spark#1218). */
export interface GrowNoteWish {
  examples: readonly GrowNoteCount[];
  wishes: string;
}

const ALL_EXAMPLES: readonly GrowNoteCount[] = [1, 2, 3];

/**
 * What tells one save of an answer from another: the answer, the examples and the wishes. The same three open
 * the note they became; another selection or another wish makes a new note (spark#1218).
 */
export function saveKey(messageId: string, wish?: GrowNoteWish): string {
  return JSON.stringify([messageId, wish?.examples ?? ALL_EXAMPLES, wish?.wishes ?? null]);
}

// An example opens with its heading and lists its beats, as the GROWTH contract has it; one line of either
// shows there is one.
const EXAMPLE_LINE = /^[ \t]{0,3}(?:#{1,6}|[-*+])[ \t]+\S/m;

/**
 * Whether an answer may be saved: it arrived, it was neither stopped nor failed, and it holds an example
 * (spark#1218: the chip is missing only under a stopped or failed answer). A notice that a web or PubMed
 * search could not run does not count against it, since the improve answer needs neither. But agent-service
 * sends a turn it could not answer as an ordinary answer holding its apology, and a search that could not
 * run as that notice over the notice's own sentence and nothing else: neither holds an example, so neither
 * is an answer to save.
 */
export function isSavableAnswer(answer: AgentMessage | undefined): answer is AgentMessage {
  return (
    answer !== undefined &&
    answer.noticeCode !== "turn_stopped" &&
    !answer.error &&
    EXAMPLE_LINE.test(answer.content)
  );
}

/**
 * The Grow improve answer's save chip (spark#1218, "Ablauf beim Speichern"): creates a new Studio notebook
 * from the answer it is bound to, opens it at once, and starts the note request that writes the note's own
 * parts into it (@adr SPARK-0060-a-saved-grow-note-is-first-written-as-its-own-fallback@1). The chat gets no
 * turn. On a failed create the person stays in the chat with the chip still there, nothing claims a save and
 * no request starts. It writes through the apollo singleton, as the drawer's own refetch does.
 * `onBeforeNavigate` runs right before the chip takes the person to the note.
 *
 * A save the person typed (the `SaveGrowNote` tool) takes the same way with their `wish`: only the chosen
 * examples go to the note request, with the wishes, and the note holds that many. It never marks the chip as
 * used and never shows the chip's failure line; it resolves to false instead, for the panel's own notice.
 */
export function useSaveToStudio({ onBeforeNavigate }: { onBeforeNavigate?: () => void } = {}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [saving, setSaving] = useState<string | null>(null);
  const [failedFor, setFailedFor] = useState<string | null>(null);
  // The notebook each save became, by `saveKey`: a click on the used chip, or the same typed save again, opens
  // it instead of creating the same one again. The ref answers a click before React has rendered; the state
  // draws the used chip, so only the chip's own saves go there.
  const savedRef = useRef(new Map<string, string>());
  const [savedFor, setSavedFor] = useState<Record<string, string>>({});
  // The saves whose notebook is being created, by `saveKey`, for the same reason a ref.
  const inFlightRef = useRef(new Set<string>());

  const open = (notebookId: string) => {
    onBeforeNavigate?.();
    navigate(`/save/studio?notebook=${encodeURIComponent(notebookId)}`);
  };

  const save = async (chip: GrowSaveTarget, wish?: GrowNoteWish): Promise<boolean> => {
    const key = saveKey(chip.messageId, wish);
    const fail = () => {
      if (!wish) setFailedFor(chip.messageId);
      return false;
    };
    const saved = savedRef.current.get(key);
    if (saved) {
      open(saved);
      return true;
    }
    if (inFlightRef.current.has(key)) return true;
    const answer = useAgentStore.getState().messages.find((m) => m.id === chip.messageId);
    if (!isSavableAnswer(answer)) return fail();
    // The compact scenes only: not the summary, not the hint, not the improve turn's hidden brief. A typed
    // save keeps only the examples it chose.
    const examples = selectImproveExamples(
      splitImproveAnswer(answer.content, chip.notebook.saveHint).examples,
      wish?.examples ?? ALL_EXAMPLES,
    );
    if (examples === null) return fail();
    const items = (wish?.examples.length ?? GROW_NOTE_MAX_ITEMS) as GrowNoteCount;
    const headings: GrowNotebookHeadings = {
      definition: intl.formatMessage({ id: "grow.notebook.definition" }),
      leadIn: intl.formatMessage({ id: "grow.answer.leadIn" }),
      growthAreas: intl.formatMessage({ id: "grow.answer.growthAreas" }),
      summary: intl.formatMessage({ id: "grow.notebook.summary" }),
      notWritten: intl.formatMessage({ id: "grow.notebook.notWritten" }),
      sources: intl.formatMessage({ id: "grow.notebook.sources" }),
    };
    // Created as its own fallback: what stays if the note's own parts are never written.
    const initial = composeGrowNotebook(chip.notebook, headings);
    // The local date in ISO form and the 24-hour time to the minute, whatever the app's language, since a
    // person may save several notes a day (spark#1218, "Name des Notizbuchs").
    const name = intl.formatMessage({ id: "grow.notebook.name" }, { name: chip.notebook.powerName, ...nowIsoMinute() });
    inFlightRef.current.add(key);
    if (!wish) {
      setSaving(chip.messageId);
      setFailedFor(null);
    }
    try {
      const { data } = await apolloClient.mutate({
        mutation: CreateStudioNotebookDocument,
        variables: { input: { data: { name, content: initial } } },
      });
      const created = data?.createStudioNotebook;
      if (created?.__typename !== "CreateStudioNotebookResult") throw new Error("not created");
      const id = created.result.id;
      savedRef.current.set(key, id);
      if (!wish) setSavedFor((current) => ({ ...current, [chip.messageId]: id }));
      startGrowNoteRun({
        notebookId: id,
        initial: created.result.content ?? initial,
        line: headings.notWritten,
        message: growNoteMessage(chip.note.briefs[items], examples),
        context: chip.note.context,
        ...(wish ? { items, wishes: wish.wishes } : {}),
        compose: (parts) => composeGrowNotebook(chip.notebook, headings, parts),
      });
      open(id);
      return true;
    } catch {
      return fail();
    } finally {
      inFlightRef.current.delete(key);
      if (!wish) setSaving((current) => (current === chip.messageId ? null : current));
    }
  };

  return { save, saving, failedFor, savedFor };
}
